import { useEffect, useMemo, useState, type Key } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  InboxOutlined,
  MinusOutlined,
  PlusOutlined,
  QrcodeOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { NumericInput, PageContainer, PageHeader, QuantityStepperInput, withDevModalFilePath } from '../components/common';
import { StockAdjustmentModal } from '../components/parts/StockAdjustmentModal';
import { useResponsive } from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Modal } = withDevModalFilePath('client/src/pages/PurchaseOrderCategoryOrdersPage.tsx');

interface PartCategoryRow {
  id: string;
  categoryLabel: string;
}

interface CategoryOrderPart {
  id: string;
  displayId: string;
  name: string;
  partNumber?: string | null;
  supplierId?: string | null;
  supplier?: {
    id: string;
    name: string;
    displayId?: string | null;
  } | null;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  unitPrice?: string | null;
  unit?: string | null;
}

type StockFilter = 'all' | 'shortage' | 'out' | 'available' | 'missingSupplier';
type SortKey = 'displayIdAsc' | 'stockGapDesc' | 'nameAsc' | 'supplierAsc';
type BulkStockAction = 'consume' | 'add';

interface SelectedSupplierGroup {
  supplierId: string;
  supplierName: string;
  supplierDisplayId?: string | null;
  parts: CategoryOrderPart[];
}

interface BulkStockFormValues {
  operatedAt: Dayjs;
  notes?: string;
  quantities?: Record<string, number | null | undefined>;
}

interface BulkStockEntry {
  part: CategoryOrderPart;
  quantity: number;
}

const normalizeNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const formatCurrency = (value: number): string => `¥${Math.round(value).toLocaleString()}`;

const getSuggestedOrderQuantity = (part: CategoryOrderPart): number => {
  const currentStock = normalizeNumber(part.currentStockLevel);
  const optimalStock = normalizeNumber(part.optimalStockLevel);
  return Math.max(optimalStock - currentStock, 1);
};

const getStockState = (part: CategoryOrderPart): StockFilter => {
  const currentStock = normalizeNumber(part.currentStockLevel);
  const optimalStock = normalizeNumber(part.optimalStockLevel);
  if (currentStock === 0) return 'out';
  if (currentStock < optimalStock) return 'shortage';
  return 'available';
};

const getStockGap = (part: CategoryOrderPart): number =>
  normalizeNumber(part.optimalStockLevel) - normalizeNumber(part.currentStockLevel);

const getPartSortId = (part: CategoryOrderPart): string => part.displayId || part.id;

const compareByPartId = (a: CategoryOrderPart, b: CategoryOrderPart): number =>
  getPartSortId(a).localeCompare(getPartSortId(b), 'ja', {
    numeric: true,
    sensitivity: 'base',
  });

export function PurchaseOrderCategoryOrdersPage() {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const { isMobile } = useResponsive();
  const [categorySearch, setCategorySearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<StockFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('displayIdAsc');
  const [selectedPartIds, setSelectedPartIds] = useState<string[]>([]);
  const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = useState(false);
  const [orderQuantities, setOrderQuantities] = useState<Record<string, number>>({});
  const [consumptionTarget, setConsumptionTarget] = useState<CategoryOrderPart | null>(null);
  const [stockAdjustmentTarget, setStockAdjustmentTarget] = useState<CategoryOrderPart | null>(null);
  const [bulkStockAction, setBulkStockAction] = useState<BulkStockAction | null>(null);
  const [consumptionForm] = Form.useForm();
  const [bulkStockForm] = Form.useForm<BulkStockFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useUtils();

  const { data: partCategories = [], isLoading: isLoadingPartCategories } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  const { data: categoryOrderPartsData, isLoading: isLoadingCategoryOrderParts, refetch: refetchCategoryOrderParts } =
    trpc.parts.list.useQuery(
      {
        page: 1,
        limit: 1000,
        category: categoryId,
      },
      { enabled: Boolean(categoryId) }
    );

  const categoryRows = useMemo<PartCategoryRow[]>(
    () => (Array.isArray(partCategories) ? (partCategories as PartCategoryRow[]) : []),
    [partCategories]
  );
  const filteredCategoryRows = useMemo(() => {
    const keyword = categorySearch.trim().toLowerCase();
    if (!keyword) return categoryRows;
    return categoryRows.filter((category) =>
      category.categoryLabel.toLowerCase().includes(keyword)
    );
  }, [categoryRows, categorySearch]);
  const categoryName = categoryId
    ? categoryRows.find((category) => category.id === categoryId)?.categoryLabel ?? categoryId
    : '';
  const categoryOrderParts = useMemo<CategoryOrderPart[]>(
    () =>
      Array.isArray(categoryOrderPartsData?.items)
        ? (categoryOrderPartsData.items as unknown as CategoryOrderPart[])
        : [],
    [categoryOrderPartsData]
  );
  const supplierOptions = useMemo(() => {
    const options = new Map<string, string>();
    categoryOrderParts.forEach((part) => {
      if (part.supplierId && part.supplier?.name) {
        options.set(part.supplierId, part.supplier.name);
      }
    });
    return Array.from(options.entries()).map(([value, label]) => ({ value, label }));
  }, [categoryOrderParts]);
  const filteredCategoryOrderParts = useMemo(() => {
    const keyword = partSearch.trim().toLowerCase();
    const filtered = categoryOrderParts.filter((part) => {
      const matchesKeyword =
        !keyword ||
        part.name.toLowerCase().includes(keyword) ||
        part.displayId.toLowerCase().includes(keyword) ||
        (part.partNumber ?? '').toLowerCase().includes(keyword);
      const matchesSupplier =
        supplierFilter === 'all' ||
        (supplierFilter === 'missing' ? !part.supplierId : part.supplierId === supplierFilter);
      const stockState = getStockState(part);
      const matchesStock =
        stockFilter === 'all' ||
        stockState === stockFilter ||
        (stockFilter === 'missingSupplier' && !part.supplierId);

      return matchesKeyword && matchesSupplier && matchesStock;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === 'displayIdAsc') {
        return compareByPartId(a, b);
      }
      if (sortKey === 'nameAsc') {
        return a.name.localeCompare(b.name, 'ja') || compareByPartId(a, b);
      }
      if (sortKey === 'supplierAsc') {
        return (a.supplier?.name ?? '未設定').localeCompare(b.supplier?.name ?? '未設定', 'ja')
          || compareByPartId(a, b);
      }
      return getStockGap(b) - getStockGap(a) || compareByPartId(a, b);
    });
  }, [categoryOrderParts, partSearch, sortKey, stockFilter, supplierFilter]);
  const selectedParts = useMemo(
    () => categoryOrderParts.filter((part) => selectedPartIds.includes(part.id)),
    [categoryOrderParts, selectedPartIds]
  );
  const selectedSupplierGroups = useMemo<SelectedSupplierGroup[]>(() => {
    const groups = new Map<string, SelectedSupplierGroup>();

    selectedParts.forEach((part) => {
      if (!part.supplierId) return;

      const existing = groups.get(part.supplierId);
      if (existing) {
        existing.parts.push(part);
        return;
      }

      groups.set(part.supplierId, {
        supplierId: part.supplierId,
        supplierName: part.supplier?.name ?? '購入先未登録',
        supplierDisplayId: part.supplier?.displayId,
        parts: [part],
      });
    });

    return Array.from(groups.values());
  }, [selectedParts]);
  const selectedSupplierCount = selectedSupplierGroups.length;
  const selectedMissingSupplierCount = selectedParts.filter((part) => !part.supplierId).length;
  const selectedSummaryText = `選択中 ${selectedParts.length} 件 / 発注先 ${selectedSupplierCount} 社 / 購入先未設定 ${selectedMissingSupplierCount} 件`;
  const shortageCount = categoryOrderParts.filter((part) => getStockState(part) === 'shortage').length;
  const outOfStockCount = categoryOrderParts.filter((part) => getStockState(part) === 'out').length;
  const missingSupplierCount = categoryOrderParts.filter((part) => !part.supplierId).length;

  useEffect(() => {
    setSelectedPartIds([]);
    setOrderQuantities({});
    setPartSearch('');
    setSupplierFilter('all');
    setStockFilter('all');
    setSortKey('displayIdAsc');
  }, [categoryId]);

  useEffect(() => {
    if (selectedParts.length === 0) {
      setIsSelectionDrawerOpen(false);
      return;
    }

    if (!isMobile) {
      setIsSelectionDrawerOpen(true);
    }
  }, [isMobile, selectedParts.length]);

  const createConsumptionMutation = trpc.partConsumptionLogs.create.useMutation({
    onSuccess: async () => {
      messageApi.success('消費記録を保存しました');
      setConsumptionTarget(null);
      consumptionForm.resetFields();
      await Promise.all([
        refetchCategoryOrderParts(),
        utils.parts.list.invalidate(),
      ]);
    },
    onError: (error) => {
      messageApi.error(`消費記録に失敗しました: ${error.message}`);
    },
  });
  const createBulkConsumptionMutation = trpc.partConsumptionLogs.create.useMutation();
  const createStockLogMutation = trpc.partStockLogs.create.useMutation();

  const getStockLevelTag = (current: number | null | undefined, optimal: number | null | undefined) => {
    const currentStock = current ?? 0;
    const optimalStock = optimal ?? 0;

    if (currentStock === 0) {
      return <Tag icon={<ExclamationCircleOutlined />} color="error">在庫切れ</Tag>;
    }
    if (currentStock < optimalStock) {
      return <Tag icon={<WarningOutlined />} color="warning">在庫不足</Tag>;
    }
    return <Tag icon={<CheckCircleOutlined />} color="success">在庫あり</Tag>;
  };

  const getOrderQuantity = (part: CategoryOrderPart) =>
    orderQuantities[part.id] ?? getSuggestedOrderQuantity(part);

  const handleProceed = (partIds: string[]) => {
    if (!categoryId || partIds.length === 0) return;

    const params = new URLSearchParams();
    params.set('partIds', partIds.join(','));
    params.set('order', 'category');
    params.set('category', categoryId);
    const quantities = partIds
      .map((partId) => {
        const part = categoryOrderParts.find((candidate) => candidate.id === partId);
        const quantity = part ? getOrderQuantity(part) : normalizeNumber(orderQuantities[partId], 1);
        return `${partId}:${Math.max(quantity, 1)}`;
      })
      .join(',');
    if (quantities) {
      params.set('quantities', quantities);
    }

    navigate(`/app/purchase-orders/create?${params.toString()}`);
  };

  const handleSelectionChange = (keys: Key[]) => {
    const nextIds = keys.map((key) => String(key));
    setSelectedPartIds(nextIds);
    setOrderQuantities((current) => {
      const next = { ...current };
      nextIds.forEach((partId) => {
        if (next[partId] == null) {
          const part = categoryOrderParts.find((candidate) => candidate.id === partId);
          next[partId] = part ? getSuggestedOrderQuantity(part) : 1;
        }
      });
      return next;
    });
  };

  const handleRecordConsumption = (part: CategoryOrderPart) => {
    setConsumptionTarget(part);
    consumptionForm.setFieldsValue({
      quantity: 1,
      consumedAt: dayjs(),
      notes: undefined,
    });
  };

  const handleOpenStockAdjustment = (part: CategoryOrderPart) => {
    setStockAdjustmentTarget(part);
  };

  const handleCloseStockAdjustment = () => {
    setStockAdjustmentTarget(null);
  };

  const handleStockAdjustmentSuccess = async () => {
    setStockAdjustmentTarget(null);
    await Promise.all([
      refetchCategoryOrderParts(),
      utils.parts.list.invalidate(),
    ]);
  };

  const resetBulkStockForm = () => {
    const quantities = Object.fromEntries(
      filteredCategoryOrderParts.map((part) => [part.id, 0])
    );
    bulkStockForm.setFieldsValue({
      operatedAt: dayjs(),
      notes: undefined,
      quantities,
    });
  };

  const handleOpenBulkStockModal = (action: BulkStockAction) => {
    setBulkStockAction(action);
    resetBulkStockForm();
  };

  const handleCancelBulkStock = () => {
    setBulkStockAction(null);
    bulkStockForm.resetFields();
  };

  const getBulkStockEntries = (values: BulkStockFormValues): BulkStockEntry[] =>
    filteredCategoryOrderParts
      .map((part) => ({
        part,
        quantity: normalizeNumber(values.quantities?.[part.id]),
      }))
      .filter((entry) => entry.quantity > 0);

  const handleSaveBulkStock = async () => {
    if (!bulkStockAction) return;

    try {
      const values = await bulkStockForm.validateFields();
      const entries = getBulkStockEntries(values);
      const actionLabel = bulkStockAction === 'consume' ? '消費' : '追加';

      if (entries.length === 0) {
        messageApi.warning(`${actionLabel}数が1以上の品目がありません`);
        return;
      }

      const operatedAt = values.operatedAt.toDate();
      const notes = values.notes?.trim() ? values.notes.trim() : undefined;

      if (bulkStockAction === 'consume') {
        await Promise.all(
          entries.map(({ part, quantity }) =>
            createBulkConsumptionMutation.mutateAsync({
              partId: part.id,
              quantity,
              consumedAt: operatedAt,
              notes,
            })
          )
        );
        messageApi.success(`一括消費を${entries.length}件保存しました`);
      } else {
        await Promise.all(
          entries.map(({ part, quantity }) => {
            const input = {
              partId: part.id,
              transactionType: 'in',
              quantity,
              referenceType: 'manual',
              createdAt: operatedAt,
              notes,
            } as Parameters<typeof createStockLogMutation.mutateAsync>[0] & { createdAt: Date };
            return createStockLogMutation.mutateAsync(input);
          })
        );
        messageApi.success(`一括追加を${entries.length}件保存しました`);
      }

      setBulkStockAction(null);
      bulkStockForm.resetFields();
      await Promise.all([
        refetchCategoryOrderParts(),
        utils.parts.list.invalidate(),
      ]);
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }

      const actionLabel = bulkStockAction === 'consume' ? '消費' : '追加';
      const errorMessage = error instanceof Error ? error.message : '保存に失敗しました';
      messageApi.error(`一括${actionLabel}に失敗しました: ${errorMessage}`);
    }
  };

  const handleOrderQuantityChange = (partId: string, value: number | string | null) => {
    const numericValue =
      typeof value === 'number' ? value : value ? Number(value) : 0;
    setOrderQuantities((current) => ({
      ...current,
      [partId]: Number.isFinite(numericValue) ? Math.max(numericValue, 0) : 0,
    }));
  };

  const handleAdjustOrderQuantity = (part: CategoryOrderPart, delta: number) => {
    handleOrderQuantityChange(part.id, Math.max(getOrderQuantity(part) + delta, 0));
  };

  const renderMobileOrderQuantityControl = (record: CategoryOrderPart) => (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        発注数量
      </Text>
      <Space.Compact style={{ width: '100%' }}>
        <Button
          htmlType="button"
          icon={<MinusOutlined aria-hidden="true" />}
          aria-label={`${record.name}の発注数量を1減らす`}
          onClick={() => handleAdjustOrderQuantity(record, -1)}
        />
        <NumericInput
          min={0}
          value={getOrderQuantity(record)}
          onChange={(value) => handleOrderQuantityChange(record.id, value)}
          aria-label={`${record.name}の発注数量`}
          style={{ width: '100%' }}
        />
        <Button
          htmlType="button"
          icon={<PlusOutlined aria-hidden="true" />}
          aria-label={`${record.name}の発注数量を1増やす`}
          onClick={() => handleAdjustOrderQuantity(record, 1)}
        />
      </Space.Compact>
      <Text type="secondary" style={{ fontSize: 12 }}>
        単位: {record.unit ?? '個'}
      </Text>
    </Space>
  );

  const handleCancelConsumption = () => {
    setConsumptionTarget(null);
    consumptionForm.resetFields();
  };

  const handleCreateConsumption = async () => {
    if (!consumptionTarget) return;

    try {
      const values = await consumptionForm.validateFields();
      createConsumptionMutation.mutate({
        partId: consumptionTarget.id,
        quantity: values.quantity,
        consumedAt: values.consumedAt.toDate(),
        notes: values.notes,
      });
    } catch {
      // Ant Design Form handles validation messages.
    }
  };

  const categoryColumns: ResponsiveColumnType<PartCategoryRow>[] = [
    {
      title: 'カテゴリ名',
      dataIndex: 'categoryLabel',
      key: 'categoryLabel',
      width: 280,
      fixed: 'left',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      mobileRole: 'hidden',
      mobilePriority: 2,
      render: (_, record) => (
        <Button
          icon={<AppstoreOutlined aria-hidden="true" />}
          aria-label={`${record.categoryLabel}の発注・消費・追加を開く`}
          onClick={() =>
            navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(record.id)}`)
          }
        >
          発注・消費・追加を開く
        </Button>
      ),
    },
  ];

  const partColumns: ResponsiveColumnType<CategoryOrderPart>[] = [
    {
      title: '部品・消耗品名',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      fixed: 'left',
      mobileRole: 'title',
      mobilePriority: 1,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: '型式',
      dataIndex: 'partNumber',
      key: 'partNumber',
      width: 140,
      mobileRole: 'description',
      mobilePriority: 3,
      render: (value) => value || '-',
    },
    {
      title: '購入先',
      key: 'supplier',
      width: 180,
      mobileRole: 'description',
      mobilePriority: 2,
      render: (_, record) => record.supplier?.name ?? <Text type="secondary">未設定</Text>,
    },
    {
      title: '単価(税込)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      align: 'right',
      mobileRole: 'description',
      mobilePriority: 3,
      render: (value: CategoryOrderPart['unitPrice']) =>
        value == null || value === '' ? (
          <Text type="secondary">未設定</Text>
        ) : (
          formatCurrency(normalizeNumber(value))
        ),
    },
    {
      title: '在庫状況',
      key: 'stock',
      width: 150,
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {getStockLevelTag(record.currentStockLevel, record.optimalStockLevel)}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.currentStockLevel ?? 0} / {record.optimalStockLevel ?? 0} {record.unit ?? '個'}
          </Text>
        </Space>
      ),
    },
    {
      title: '消費',
      key: 'consumptionLog',
      width: 120,
      mobileRole: 'hidden',
      mobilePriority: 4,
      render: (_, record) => (
        <Button
          icon={<HistoryOutlined aria-hidden="true" />}
          aria-label={`${record.name}の消費を記録`}
          onClick={() => handleRecordConsumption(record)}
        >
          消費
        </Button>
      ),
    },
    {
      title: '発注数',
      key: 'orderQuantity',
      width: 130,
      mobileRole: 'hidden',
      mobilePriority: 4,
      render: (_, record) => (
        <NumericInput
          min={0}
          value={getOrderQuantity(record)}
          onChange={(value) => handleOrderQuantityChange(record.id, value)}
          aria-label={`${record.name}の発注数量`}
          addonAfter={record.unit ?? '個'}
          style={{ width: 112 }}
        />
      ),
    },
    {
      title: '追加',
      key: 'stockAdjustment',
      width: 120,
      mobileRole: 'hidden',
      mobilePriority: 4,
      render: (_, record) => (
        <Button
          icon={<InboxOutlined aria-hidden="true" />}
          aria-label={`${record.name}の在庫を追加`}
          onClick={() => handleOpenStockAdjustment(record)}
        >
          追加
        </Button>
      ),
    },
  ];

  const renderSelectedSupplierGroups = () => (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Text strong>発注先ごとの候補</Text>
      </div>

      {selectedSupplierGroups.length === 0 && (
        <Text type="secondary">発注対象の品目を選択してください。</Text>
      )}

      {selectedSupplierGroups.map((group) => {
        const subtotal = group.parts.reduce(
          (sum, part) => sum + getOrderQuantity(part) * normalizeNumber(part.unitPrice),
          0
        );

        return (
          <Card
            key={group.supplierId}
            size="small"
            title={
              <Space wrap>
                <Text strong>{group.supplierName}</Text>
                <Tag color="green">{group.parts.length}件</Tag>
              </Space>
            }
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {group.parts.map((part) => (
                <Row key={part.id} justify="space-between" gutter={8}>
                  <Col flex="1 1 auto">
                    <Space direction="vertical" size={0}>
                      <Text>{part.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        単価(税込) {formatCurrency(normalizeNumber(part.unitPrice))}
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Text strong>
                      {getOrderQuantity(part)} {part.unit ?? '個'}
                    </Text>
                  </Col>
                </Row>
              ))}
              <Row justify="space-between" style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                <Col>
                            <Text type="secondary">小計(税込)</Text>
                </Col>
                <Col>
                  <Text strong>{formatCurrency(subtotal)}</Text>
                </Col>
              </Row>
              <Button
                block
                type="primary"
                onClick={() => handleProceed(group.parts.map((part) => part.id))}
              >
                この発注先でドラフト作成
              </Button>
            </Space>
          </Card>
        );
      })}

      {selectedMissingSupplierCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message="確認が必要"
          description="購入先未設定の品目は発注書ドラフトに含めません。先に部品マスタで購入先を設定してください。"
        />
      )}
    </Space>
  );

  const bulkStockActionLabel = bulkStockAction === 'add' ? '追加' : '消費';
  const isBulkStockSaving =
    createBulkConsumptionMutation.isPending || createStockLogMutation.isPending;
  const renderBulkStockActionButtons = (variant: 'header' | 'mobile') => {
    const isMobileVariant = variant === 'mobile';

    return (
      <Space
        data-testid={isMobileVariant ? 'bulk-stock-mobile-actions' : 'bulk-stock-header-actions'}
        direction={isMobileVariant ? 'vertical' : 'horizontal'}
        size={8}
        wrap={!isMobileVariant}
        style={{ width: isMobileVariant ? '100%' : undefined }}
      >
        <Button
          block={isMobileVariant}
          size={isMobileVariant ? 'middle' : 'small'}
          icon={<HistoryOutlined aria-hidden="true" />}
          disabled={filteredCategoryOrderParts.length === 0}
          onClick={() => handleOpenBulkStockModal('consume')}
        >
          一括消費を記録
        </Button>
        <Button
          block={isMobileVariant}
          size={isMobileVariant ? 'middle' : 'small'}
          icon={<InboxOutlined aria-hidden="true" />}
          disabled={filteredCategoryOrderParts.length === 0}
          onClick={() => handleOpenBulkStockModal('add')}
        >
          一括追加を記録
        </Button>
        {!isMobileVariant && selectedParts.length > 0 ? (
          <Button
            size="small"
            icon={<ShoppingCartOutlined aria-hidden="true" />}
            onClick={() => setIsSelectionDrawerOpen(true)}
          >
            選択中 {selectedParts.length}件
          </Button>
        ) : null}
      </Space>
    );
  };

  return (
    <PageContainer>
      {contextHolder}

      <PageHeader
        title="カテゴリ別 発注・消費・追加"
        extra={
          <Space wrap>
            <Button
              icon={<QrcodeOutlined aria-hidden="true" />}
              onClick={() => navigate('/app/qr-labels#part-category-order')}
            >
              カテゴリ発注QRを印刷
            </Button>
            <Button onClick={() => navigate('/app/purchase-orders')}>発注一覧に戻る</Button>
          </Space>
        }
      />

      {!categoryId && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]} align="middle" justify="space-between">
              <Col flex="1 1 320px">
                <Space direction="vertical" size={4}>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    発注・消費記録を行うカテゴリを選択
                  </Title>
                  <Text type="secondary">
                    カテゴリ名で絞り込み、必要な品目の発注・消費記録一覧を開きます。
                  </Text>
                </Space>
              </Col>
              <Col flex="0 1 360px">
                <Input
                  allowClear
                  prefix={<SearchOutlined aria-hidden="true" />}
                  placeholder="カテゴリ名で検索"
                  value={categorySearch}
                  onChange={(event) => setCategorySearch(event.target.value)}
                />
              </Col>
            </Row>
          </Card>

          <Card>
            <ResponsiveTable<PartCategoryRow>
              columns={categoryColumns}
              dataSource={filteredCategoryRows}
              loading={isLoadingPartCategories}
              rowKey="id"
              scrollX={560}
              mobileCardConfig={{
                titleField: (record) => <Text strong>{record.categoryLabel}</Text>,
                actions: (record) => (
                  <Button
                    icon={<AppstoreOutlined aria-hidden="true" />}
                    aria-label={`${record.categoryLabel}の発注・消費・追加を開く`}
                    onClick={() =>
                      navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(record.id)}`)
                    }
                  >
                    発注・消費・追加を開く
                  </Button>
                ),
                onClick: (record) =>
                  navigate(`/app/purchase-orders/category-orders/${encodeURIComponent(record.id)}`),
              }}
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (total) => `全 ${total} 件`,
              }}
            />
          </Card>
        </>
      )}

      {categoryId && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]} align="middle" justify="space-between">
              <Col flex="1 1 320px">
                <Space direction="vertical" size={4}>
                  <Title level={5} style={{ marginBottom: 0 }}>
                    {`カテゴリ別 発注・消費・追加: ${categoryName}`}
                  </Title>
                </Space>
              </Col>
              <Col>
                <Button onClick={() => navigate('/app/purchase-orders/category-orders')}>
                  カテゴリ一覧に戻る
                </Button>
              </Col>
            </Row>
          </Card>

          <Card>
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} lg={6}>
                <Input
                  allowClear
                  prefix={<SearchOutlined aria-hidden="true" />}
                  placeholder="部品名・型式で検索"
                  value={partSearch}
                  onChange={(event) => setPartSearch(event.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  style={{ width: '100%' }}
                  value={supplierFilter}
                  onChange={setSupplierFilter}
                  options={[
                    { value: 'all', label: '購入先　すべて' },
                    ...supplierOptions,
                    { value: 'missing', label: '購入先未設定' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  style={{ width: '100%' }}
                  value={stockFilter}
                  onChange={setStockFilter}
                  options={[
                    { value: 'all', label: '状態　すべて' },
                    { value: 'shortage', label: '在庫不足' },
                    { value: 'out', label: '在庫切れ' },
                    { value: 'available', label: '在庫あり' },
                    { value: 'missingSupplier', label: '購入先未設定' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <Select
                  style={{ width: '100%' }}
                  value={sortKey}
                  onChange={setSortKey}
                  options={[
                    { value: 'displayIdAsc', label: 'ID順' },
                    { value: 'stockGapDesc', label: '在庫不足が大きい順' },
                    { value: 'nameAsc', label: '品名順' },
                    { value: 'supplierAsc', label: '購入先順' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <Button
                  block
                  type="primary"
                  icon={<ShoppingCartOutlined aria-hidden="true" />}
                  disabled={selectedParts.length === 0}
                  onClick={() => handleProceed(selectedPartIds)}
                >
                  発注書ドラフトを作成
                </Button>
              </Col>
            </Row>
          </Card>

          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="発注候補" value={categoryOrderParts.length} suffix="件" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="在庫不足" value={shortageCount + outOfStockCount} suffix="件" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="発注先" value={supplierOptions.length} suffix="社" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="購入先未設定" value={missingSupplierCount} suffix="件" />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24}>
              <Card
                title={`${categoryName}の品目`}
                extra={isMobile ? undefined : renderBulkStockActionButtons('header')}
                style={{ height: '100%' }}
              >
                {isMobile && (
                  <div style={{ marginBottom: 12 }}>
                    {renderBulkStockActionButtons('mobile')}
                  </div>
                )}
                <ResponsiveTable<CategoryOrderPart>
                  columns={partColumns}
                  dataSource={filteredCategoryOrderParts}
                  loading={isLoadingCategoryOrderParts || isLoadingPartCategories}
                  rowKey="id"
                  scrollX={1110}
                  rowSelection={{
                    selectedRowKeys: selectedPartIds,
                    onChange: handleSelectionChange,
                    preserveSelectedRowKeys: true,
                  }}
                  mobileCardConfig={{
                    titleField: (record) => <Text strong>{record.name}</Text>,
                    actions: (record) => (
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {renderMobileOrderQuantityControl(record)}
                        <Space.Compact style={{ width: '100%' }}>
                          <Button
                            size="small"
                            icon={<HistoryOutlined aria-hidden="true" />}
                            aria-label={`${record.name}の消費を記録`}
                            onClick={() => handleRecordConsumption(record)}
                          >
                            消費
                          </Button>
                          <Button
                            size="small"
                            icon={<InboxOutlined aria-hidden="true" />}
                            aria-label={`${record.name}の在庫を追加`}
                            onClick={() => handleOpenStockAdjustment(record)}
                          >
                            追加
                          </Button>
                        </Space.Compact>
                      </Space>
                    ),
                  }}
                  pagination={{
                    pageSize: 100,
                    showSizeChanger: false,
                    showTotal: (total) => `全 ${total} 件`,
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Drawer
            title="選択中の発注候補"
            placement="right"
            width={400}
            open={!isMobile && isSelectionDrawerOpen && selectedParts.length > 0}
            onClose={() => setIsSelectionDrawerOpen(false)}
            mask={false}
            destroyOnHidden
            styles={{
              body: { paddingBottom: 24 },
            }}
          >
            <div data-testid="selected-parts-drawer">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text strong>{selectedSummaryText}</Text>
                {renderSelectedSupplierGroups()}
              </Space>
            </div>
          </Drawer>

          {isMobile && selectedParts.length > 0 && (
            <Card
              size="small"
              style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                boxShadow: '0 -8px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Row gutter={[12, 8]} align="middle">
                <Col flex="1">
                  <Text strong>選択中 {selectedParts.length}件</Text>
                  <br />
                  <Text type="secondary">発注先 {selectedSupplierCount}社</Text>
                </Col>
                <Col>
                  <Button type="primary" onClick={() => handleProceed(selectedPartIds)}>
                    発注書ドラフトを作成
                  </Button>
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}

      <Modal
        title={`部品・消耗品消費記録: ${consumptionTarget?.name ?? ''}`}
        open={Boolean(consumptionTarget)}
        onCancel={handleCancelConsumption}
        onOk={handleCreateConsumption}
        confirmLoading={createConsumptionMutation.isPending}
        okText="記録"
        cancelText="キャンセル"
      >
        <Form form={consumptionForm} layout="vertical" style={{ marginTop: 16 }}>
          {consumptionTarget && (
            <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
              <Text strong>{consumptionTarget.name}</Text>
              <Text type="secondary">
                現在庫: {consumptionTarget.currentStockLevel ?? 0} / {consumptionTarget.optimalStockLevel ?? 0}{' '}
                {consumptionTarget.unit ?? '個'}
              </Text>
            </Space>
          )}

          <Form.Item
            label="消費数"
            name="quantity"
            rules={[
              { required: true, message: '消費数を入力してください' },
              { type: 'number', min: 1, message: '1以上の値を入力してください' },
            ]}
          >
            <QuantityStepperInput
              min={1}
              precision={0}
              style={{ width: '100%' }}
              placeholder="消費数を入力"
              addonAfter={consumptionTarget?.unit ?? '個'}
              aria-label={`${consumptionTarget?.name ?? '部品・消耗品'}の消費数`}
              incrementAriaLabel={`${consumptionTarget?.name ?? '部品・消耗品'}の消費数を1増やす`}
            />
          </Form.Item>

          <Form.Item
            label="消費日時"
            name="consumedAt"
            rules={[{ required: true, message: '消費日時を選択してください' }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <TextArea rows={3} placeholder="消費理由や作業内容を入力" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`一括${bulkStockActionLabel}記録: ${categoryName}`}
        open={Boolean(bulkStockAction)}
        onCancel={handleCancelBulkStock}
        onOk={handleSaveBulkStock}
        confirmLoading={isBulkStockSaving}
        okText={`入力した${bulkStockActionLabel}を保存`}
        cancelText="キャンセル"
        width={isMobile ? 'calc(100vw - 24px)' : 760}
        style={{
          top: isMobile ? 12 : undefined,
          maxWidth: isMobile ? 'calc(100vw - 24px)' : undefined,
        }}
        styles={{
          body: {
            padding: isMobile ? '12px 12px 0' : undefined,
          },
        }}
      >
        <Form form={bulkStockForm} layout="vertical" style={{ marginTop: isMobile ? 8 : 16 }}>
          <Alert
            type="info"
            showIcon
            message={`表示中の品目 ${filteredCategoryOrderParts.length} 件が対象です`}
            description={`${bulkStockActionLabel}数が0または空欄の品目は保存しません。日時と備考は保存対象の品目すべてに共通で記録します。`}
            style={{ marginBottom: isMobile ? 12 : 16 }}
          />

          <Row gutter={[12, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={`${bulkStockActionLabel}日時`}
                name="operatedAt"
                rules={[{ required: true, message: `${bulkStockActionLabel}日時を選択してください` }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="備考" name="notes">
                <TextArea rows={3} placeholder="共通備考を入力" />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              maxHeight: isMobile ? '34vh' : 380,
              overflowY: 'auto',
            }}
          >
            {filteredCategoryOrderParts.map((part, index) => (
              <Row
                key={part.id}
                gutter={[12, 8]}
                align={isMobile ? 'top' : 'middle'}
                style={{
                  padding: isMobile ? '12px 10px 0' : '12px 12px 0',
                  borderBottom:
                    index === filteredCategoryOrderParts.length - 1 ? undefined : '1px solid #f0f0f0',
                }}
              >
                <Col xs={24} md={15}>
                  <Space direction="vertical" size={2}>
                    <Text strong>{part.name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      現在庫: {part.currentStockLevel ?? 0} / {part.optimalStockLevel ?? 0}{' '}
                      {part.unit ?? '個'}
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} md={9}>
                  <Form.Item
                    label={`${bulkStockActionLabel}数`}
                    name={['quantities', part.id]}
                    rules={[{ type: 'number', min: 0, message: '0以上の値を入力してください' }]}
                  >
                    <QuantityStepperInput
                      min={0}
                      precision={0}
                      addonAfter={part.unit ?? '個'}
                      aria-label={`${part.name}の一括${bulkStockActionLabel}数`}
                      incrementAriaLabel={`${part.name}の一括${bulkStockActionLabel}数を1増やす`}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </div>
        </Form>
      </Modal>

      <StockAdjustmentModal
        open={Boolean(stockAdjustmentTarget)}
        part={stockAdjustmentTarget}
        onClose={handleCloseStockAdjustment}
        onSuccess={handleStockAdjustmentSuccess}
      />
    </PageContainer>
  );
}
